import React, { useEffect, useMemo, useState } from 'react';
import TableWithConfig from '@components/TableWithConfig';
import { Dialog, Checkbox, FormControlLabel } from '@material-ui/core';
import { Button, IconButton} from '@coolremote/shared';
import { t } from 'ttag';
import {
  DISPLAY_TEMPLATE_TYPE,
  nestingConfig,
  parseTemplatesForExport,
  TEMPLATE_TYPE,
} from '../..';

import { downloadJson } from '@services/utils';
import useStyles from './styles';
import { Close } from '@material-ui/icons';
import { useStoreActions, useStoreState } from '@models/RootStore';
import useBacNet from '@hooks/useBacNet';
import moment from 'moment';

/**
 * Generates a filtered tree of selected templates based on the provided selection map.
 * Recursively processes nested UI templates and preserves the hierarchical structure.
 *
 * @param {Array<Object>} templatesTree - The complete tree of template objects to filter from
 * @param {Object<string, boolean>} selectedTemplates - A map of template IDs to their selection status
 * @returns {Array<Object>} A filtered array of template objects containing only selected items with their relevant properties.
 *                          For PARAM_TEMPLATE type: includes name, model, vendorId, and data.
 *                          For other types: includes name, fileId, and data.
 *                          Nested uiTemplates are recursively filtered and included if present.
 */
const generateSelectedTemplatesTree = (templatesTree, selectedTemplates) => {
  return templatesTree
    .map((template) => {
      const isSelected = selectedTemplates[template.id];
      if (!isSelected) return null;

      const newTemplate =
        template.type === TEMPLATE_TYPE.PARAM_TEMPLATE
          ? {
            name: template.name,
            model: template.model,
            vendorId: template.vendorId,
            vendorName: template.vendorName,
            data: template.data,
            entityTemplate: !!template.entityTemplate,
          }
          : {
            name: template.name,
            fileId: template.fileId,
            data: template.data,
          };

      if (template.uiTemplates && template.uiTemplates.length) {
        const exportedUiTemplates = generateSelectedTemplatesTree(
          template.uiTemplates,
          selectedTemplates
        ).filter(Boolean);

        if (exportedUiTemplates.length) {
          newTemplate.uiTemplates = exportedUiTemplates;
        }
      }

      return newTemplate;
    })
    .filter(Boolean);
};

const ImportExportDialog = ({
  mode = 'export',
  templatesData,
  open,
  onClose,
  importedData,
  onImportComplete
}) => {
  const classes = useStyles();

  const [selectedTemplates, setSelectedTemplates] = useState({});
  const [loading, setLoading] = useState(false);

  const {siteId} = useStoreState(
    (action) => action.selections.selections
  );

  const allSites = useStoreState(
    (state) => state.sites.allSites
  );

  const {importBacnetTemplates} = useBacNet();

  const addMessage = useStoreActions(
    (action) => action.errorMessage.addMessage
  );

  const parsedImportedData = useMemo(() => {
    if (!importedData) return [];

    const generateUniqueId = (name) => {
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      return `${name}_${randomNum}`;
    };

    const addUniqueIds = (templates) => {
      return templates.map((template) => {
        const templateWithId = {
          ...template,
          id: generateUniqueId(template.name),
        };

        if (template['ui-templates'] && template['ui-templates'].length) {
          templateWithId.uiTemplates = addUniqueIds(template['ui-templates']);
        }

        return templateWithId;
      });
    };

    const inheritParentProperties = (templates) => {
      return templates.map((template) => {
        const templateWithInheritance = {
          ...template,
          type: TEMPLATE_TYPE.PARAM_TEMPLATE,
        };

        if (template.uiTemplates && template.uiTemplates.length) {
          templateWithInheritance.uiTemplates = template.uiTemplates.map(
            (uiTemplate) => ({
              ...uiTemplate,
              vendorId: template.vendorId,
              model: template.model,
              vendorName: template.vendorName,
              type: TEMPLATE_TYPE.UI_TEMPLATE,
            })
          );
        }

        return templateWithInheritance;
      });
    };

    const templatesWithIds = addUniqueIds(importedData);

    return inheritParentProperties(templatesWithIds);
  }, [importedData]);

  const activeTemplatesTree = mode === 'export' ? templatesData : parsedImportedData;

  const UITemplateToParamTemplateMap = useMemo(() => {
    if (!activeTemplatesTree) return {};

    const map = {};
    activeTemplatesTree.forEach((template) => {
      template.uiTemplates?.forEach(
        (uiTemplate) => (map[uiTemplate.id] = template.id)
      );
    });
    return map;
  }, [activeTemplatesTree]);

  const renderCheckboxCell = (value, row) => {
    const isChecked = selectedTemplates[row.id] === true;

    const handleCheckboxChange = (event) => {
      const { type } = row;
      const checked = event.target.checked;

      // If UI Template is selected, ensure its parent Param Template is also selected
      if (type === TEMPLATE_TYPE.UI_TEMPLATE && checked) {
        const parentParamTemplateId = UITemplateToParamTemplateMap[row.id];
        if (parentParamTemplateId) {
          setSelectedTemplates((prevSelected) => ({
            ...prevSelected,
            [parentParamTemplateId]: true,
            [row.id]: true,
          }));
          return;
        }
      }

      // If Param Template is deselected, also deselect its child UI Templates
      if (type === TEMPLATE_TYPE.PARAM_TEMPLATE && !checked) {

        const updatedSelections = { ...selectedTemplates };

        activeTemplatesTree.forEach((template) => {
          if (template.id === row.id) {
            template.uiTemplates?.forEach((uiTemplate) => {
              delete updatedSelections[uiTemplate.id];
            });
          }
        });
        updatedSelections[row.id] = false;
        setSelectedTemplates(updatedSelections);
        return;
      }

      // Default behavior

      setSelectedTemplates({
        ...selectedTemplates,
        [row.id]: checked,
      });
    };
    return (
      <Checkbox
        type="checkbox"
        checked={isChecked}
        onChange={handleCheckboxChange}
      />
    );
  };

  const columnsConfig = [
    { id: '', label: '', width: '20px', renderCellContent: renderCheckboxCell },
    { id: 'name', label: t`Name` },
    { id: 'model', label: t`Model` },
    {
      id: 'vendorId',
      label: t`Vendor (ID)`,
      renderCellContent: (value, row) => {
        const vendorName = row.vendorName ?? t`Unknown Vendor`;
        return `${vendorName} (${value})`;
      },
    },
    {
      id: 'type',
      label: t`Type`,
      renderCellContent: (value) => DISPLAY_TEMPLATE_TYPE[value],
    },
  ];

  const handleExportClick = () => {

    try{
      const templatesToExport = generateSelectedTemplatesTree(
        templatesData,
        selectedTemplates
      );
      const fullData = {
        identifier: 'BACnet templates export',
        templates: parseTemplatesForExport(templatesToExport),
      };

      const siteName = allSites[siteId]?.name || 'UnknownSite';

      const fileName = `BACnet templates_${siteName}_${moment().format('DD.MM.YYYY_HH-mm')}.json`;
      downloadJson(fullData, fileName);
      onClose();
    } catch (error) {
      console.error('Error during export:', error);
      addMessage({ message: t`An error occurred during export. Please try again.`});
    }
  };

  const handleImportClick = async () => {
    const templatesToImport = generateSelectedTemplatesTree(
      parsedImportedData,
      selectedTemplates
    );

    const parsedTemplatesToImport = templatesToImport.map((template) => {
      const { uiTemplates, ...rest } = template;
      return {
        ...rest,
        ['ui-templates']: uiTemplates ? uiTemplates : [],
      };
    });

    setLoading(true);

    try{
      const response = await importBacnetTemplates({siteId, data: parsedTemplatesToImport});
      onClose();
      onImportComplete(response.data);
    } catch (error) {
      console.error('Error during import:', error);
      addMessage({ message: t`An error occurred during import. Please try a different file.` });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDeselectAllClick = () => {
    if (isAllSelected) {
      // Deselect all templates
      setSelectedTemplates({});
    } else {
      // Select all templates
      const allSelected = {};
      activeTemplatesTree.forEach((template) => {
        allSelected[template.id] = true;
        template.uiTemplates?.forEach((uiTemplate) => {
          allSelected[uiTemplate.id] = true;
        });
      });
      setSelectedTemplates(allSelected);
    }
  };

  // On dialog open, select all templates by default
  useEffect(() => {
    if (open) {
      const allSelected = {};
      const sourceData = mode === 'export' ? templatesData : parsedImportedData;
      sourceData.forEach((template) => {
        allSelected[template.id] = true;
        template.uiTemplates?.forEach((uiTemplate) => {
          allSelected[uiTemplate.id] = true;
        });
      });
      setSelectedTemplates(allSelected);
    } else {
      setSelectedTemplates({});
    }
  }, [open, mode, templatesData, parsedImportedData]);

  const mainButtonAction =
    mode === 'export' ? handleExportClick : handleImportClick;
  const mainButtonText = mode === 'export' ? t`Export` : t`Import`;

  const isNonSelected = useMemo(() => {
    return Object.values(selectedTemplates).every((isSelected) => !isSelected);
  }, [selectedTemplates]);

  const isAllSelected = useMemo(() => {
    const allTemplateIds = [];
    activeTemplatesTree.forEach((template) => {
      allTemplateIds.push(template.id);
      template.uiTemplates?.forEach((uiTemplate) => {
        allTemplateIds.push(uiTemplate.id);
      });
    });

    return (
      allTemplateIds.length > 0 &&
      allTemplateIds.every((id) => selectedTemplates[id])
    );
  }, [selectedTemplates]);


  return (
    <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog}>
        <div className={classes.dialogHeader}>
          {mode === 'export'
            ? t`Export BACnet Templates`
            : t`Import BACnet Templates`}
          <IconButton
            disableRipple
            className={classes.iconBtnStyle}
            onClick={onClose}
          >
            <Close color="#7f7692" />
          </IconButton>
        </div>
        <div className={classes.dialogContent}>
          <div className={classes.selectAllWrapper}>
            <FormControlLabel
              control={
                <Checkbox
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectDeselectAllClick}
                />
              }
              label={t`Select all`}
            />
          </div>

          <div className={classes.tableWrapper}>
            <TableWithConfig
              stickyHeader
              columnsConfig={columnsConfig}
              rows={activeTemplatesTree}
              nestingConfig={nestingConfig}
            />
          </div>
        </div>
        <div className={classes.dialogFooter}>
          <div className={classes.mainActionsWrapper}>
            <Button onClick={onClose} marginRight white width={150}>
              {t`Cancel`}
            </Button>
            <Button
              disabled={isNonSelected || loading}
              title={isNonSelected ? t`No templates selected` : ''}
              className={classes.mainButton}
              onClick={mainButtonAction}
              width={150}
            >
              {loading ? t`Importing...` : mainButtonText}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ImportExportDialog;
